<template>
  <div class="notification-settings page-layout-padding" data-testid="notification-settings">
    <div id="notification-clear-all-module" class="margin-bottom-40">
      <h1 class="page-title margin-bottom-20">
        {{ $getText('NOTIFICATION_CLEAR_ALL_TITLE') }}
      </h1>
      <b-row>
        <b-col>
          <div class="settings-label large mb-2">
            {{ $getText('NOTIFICATION_CLEAR_FROM') }}
          </div>
        </b-col>
      </b-row>
      <b-row align-h="start">
        <b-col cols="4">
          <aula-select
            id="notification-module-type-select"
            :value="notificationSettingModel.getSelectedAreas()"
            multiple
            @change="updateSelectedAreas"
          >
            <aula-option
              v-for="(module, index) in availableAreas"
              :key="`module-${index}`"
              :label="module.name"
              :value="module.value"
              :class="{ 'with-checkbox': true }"
            ></aula-option>
          </aula-select>
        </b-col>
        <b-col cols="2">
          <AulaButton
            class="text-uppercase"
            :disabled="notificationSettingModel.getSelectedAreas().length === 0"
            variant="primary"
            @click.native="confirmationModalOpen"
          >
            {{ $getText('NOTIFICATION_CLEAR_ALL_BUTTON') }}
          </AulaButton>
        </b-col>
      </b-row>
    </div>
    <div class="mb-1">
      <h1 class="page-title" tabindex="0">
        {{ $getText('NOTIFICATION_SETTINGS_TITLE') }}
      </h1>
      <div class="d-flex">
        <div class="settings-label large mb-2">
          {{ $getText('NOTIFICATION_SETTINGS_FROM') }}
        </div>
        <div id="notification-setting-edit-button" class="ml-auto">
          <aula-form-footer
            :is-loading="isLoading"
            :show-cancel="!isViewMode"
            :show-line="false"
            :submit-text="isViewMode ? 'BUTTON_EDIT' : 'BUTTON_SAVE'"
            class="m-0 pb-1"
            :class="'edit-mode'"
            @cancelClicked="cancelModalOkClicked()"
            @submitClicked="isViewMode ? initEditMode() : saveChanges()"
          />
        </div>
      </div>
    </div>
    <div class="notification-settings-body" :class="isViewMode ? 'view-mode' : 'edit-mode'">
      <!--      MESSAGES-->
      <template v-if="notificationSettingModel.getEmailAvailable() || notificationSettingModel.getAppAvailable()">
        <div class="settings-label">
          {{ $getText('NOTIFICATION_SETTINGS_MESSAGES') }}
        </div>
        <notification-checkbox
          id="id-employees"
          class="notification-option"
          :checked="notificationSettingModel.getMessagesFromEmployees()"
          group-text-key-name="NOTIFICATION_SETTINGS_MESSAGES"
          text-key-name="NOTIFICATION_SETTINGS_EMPLOYEES"
          :is-view-mode="isViewMode"
          @change="updateMessagesFromEmployees"
        />
        <notification-checkbox
          id="id-guardians"
          class="notification-option"
          :checked="notificationSettingModel.getMessagesFromGuardians()"
          group-text-key-name="NOTIFICATION_SETTINGS_MESSAGES"
          text-key-name="NOTIFICATION_SETTINGS_GUARDIANS"
          :is-view-mode="isViewMode"
          @change="updateMessagesFromGuardians"
        />
        <notification-checkbox
          id="id-children"
          class="notification-option"
          :checked="notificationSettingModel.getMessagesFromChildren()"
          group-text-key-name="NOTIFICATION_SETTINGS_MESSAGES"
          text-key-name="NOTIFICATION_SETTINGS_CHILDREN"
          :is-view-mode="isViewMode"
          @change="updateMessagesFromChildren"
        />
        <!--        CALENDAR-->
        <div class="settings-label">
          {{ $getText('NOTIFICATION_SETTINGS_CALENDAR') }}
        </div>
        <notification-checkbox
          id="id-events"
          class="notification-option"
          :class="{ 'single-option': profile.role !== portalRoles.EMPLOYEE }"
          :checked="notificationSettingModel.getCalendar()"
          group-text-key-name="NOTIFICATION_SETTINGS_CALENDAR"
          text-key-name="NOTIFICATION_SETTINGS_CALENDAR_EVENTS"
          :is-view-mode="isViewMode"
          @change="updateCalendar"
        />
        <notification-checkbox
          v-if="profile.role === portalRoles.EMPLOYEE"
          id="id-teacher"
          class="notification-option"
          :checked="notificationSettingModel.getNotifyAssignedAsSubstituteTeacher()"
          group-text-key-name="NOTIFICATION_SETTINGS_CALENDAR"
          text-key-name="NOTIFICATION_SETTINGS_SUBSTITUTE_TEACHER"
          :is-view-mode="isViewMode"
          @change="updateNotifyAssignedAsSubstituteTeacher"
        />
        <notification-checkbox
          v-if="profile.role === portalRoles.EMPLOYEE"
          id="id-lesson-note"
          class="notification-option"
          :checked="notificationSettingModel.getNotifyLessonNoteChanged()"
          group-text-key-name="NOTIFICATION_SETTINGS_CALENDAR"
          text-key-name="NOTIFICATION_SETTINGS_LESSON_NOTE_CHANGED"
          :is-view-mode="isViewMode"
          @change="updateNotifyLessonNoteChanged"
        />
        <!--        GALLERY-->
        <div class="settings-label">
          {{ $getText('NOTIFICATION_SETTINGS_GALLERY') }}
        </div>
        <notification-checkbox
          id="id-media"
          class="notification-option single-option"
          :checked="notificationSettingModel.getGallery()"
          group-text-key-name="NOTIFICATION_SETTINGS_GALLERY"
          text-key-name="NOTIFICATION_SETTINGS_MEDIA"
          :is-view-mode="isViewMode"
          @change="updateGallery"
        />
        <!--        OVERVIEW-->
        <div class="settings-label">
          {{ $getText('NOTIFICATION_SETTINGS_OVERVIEW') }}
        </div>
        <notification-checkbox
          id="id-post"
          class="notification-option single-option"
          :checked="notificationSettingModel.getPost()"
          group-text-key-name="NOTIFICATION_SETTINGS_OVERVIEW"
          text-key-name="NOTIFICATION_SETTINGS_POST"
          :is-view-mode="isViewMode"
          @change="updatePost"
        />
        <!--        COME AND GO-->
        <template v-if="profile.role === portalRoles.GUARDIAN || profile.role === portalRoles.OTP">
          <div class="settings-label">
            {{ $getText('PRESENCE_PAGETITLE') }}
          </div>
          <notification-checkbox
            id="id-activities"
            class="notification-option"
            :checked="notificationSettingModel.getPickUpActivity()"
            group-text-key-name="PRESENCE_PAGETITLE"
            text-key-name="NOTIFICATION_SETTINGS_PICKUP_ACTIVITIES"
            :is-view-mode="isViewMode"
            @change="updatePickUpActivity"
          />
          <notification-checkbox
            id="id-requests"
            class="notification-option second-option"
            :checked="notificationSettingModel.getVacationRequest()"
            group-text-key-name="PRESENCE_PAGETITLE"
            text-key-name="NOTIFICATION_SETTINGS_VACATION_REQUESTS"
            :is-view-mode="isViewMode"
            @change="updateVacationRequest"
          />
        </template>
        <!--        WIDGETS-->
        <template v-for="widgetSetting in notificationSettingModel.getWidgetSettings()">
          <div :key="widgetSetting.widgetId" class="settings-label">
            {{ widgetSetting.title }}
          </div>
          <notification-checkbox
            :id="`id-widget-${widgetSetting.widgetId}`"
            :key="`widget-${widgetSetting.widgetId}`"
            class="notification-option single-option"
            :checked="widgetSetting.isActive"
            group-text-key-name="NOTIFICATION_SETTINGS_WIDGET"
            text-key-name="NOTIFICATION_SETTINGS_WIDGET_ACTIVATED"
            :is-view-mode="isViewMode"
            @change="updateWidget(widgetSetting.widgetId, $event)"
          />
        </template>
        <br /><br />
      </template>
    </div>

    <div class="settings-label large">
      {{ $getText('NOTIFICATION_SETTINGS_ON') }}
    </div>
    <div class="notification-setting-areas">
      <!--        MOBILE APP-->
      <div class="settings-label">
        {{ $getText('NOTIFICATION_SETTINGS_APP') }}
      </div>
      <div v-if="notificationSettingModel.getAppAvailable()" class="settings-content py-3">
        {{ $t('settings.app_notification_description') }}
      </div>
      <template v-if="!notificationSettingModel.getAppAvailable()">
        <div class="settings-content">
          {{ $getText('NOTIFICATION_SETTINGS_NO_APP') }}
        </div>
        <div class="settings-content">
          {{ $getText('NOTIFICATION_SETTINGS_DOWNLOAD') }}
        </div>
        <a href="https://itunes.apple.com/" :aria-label="$getText('ARIA_LABEL_APP_STORE')" class="settings-content">
          <img class="image-link" src="../../../static/appstores/app_store.svg" alt="" />
        </a>
        <a
          href="https://play.google.com/store/apps/"
          :aria-label="$getText('ARIA_LABEL_GOOGLE_PLAY')"
          class="settings-content"
        >
          <img class="image-link" src="../../../static/appstores/app_google_play.svg" alt="" />
        </a>
      </template>

      <!--        EMAIL-->
      <div class="settings-label">
        {{ $getText('NOTIFICATION_SETTINGS_EMAIL') }}
      </div>
      <template v-if="!notificationSettingModel.getEmailAvailable()">
        <div class="settings-content">
          {{ $getText('NOTIFICATION_SETTINGS_NO_EMAIL') }}<br />
          {{ $getText('NOTIFICATION_SETTINGS_NO_EMAIL_2') }}
        </div>
      </template>
      <template v-if="notificationSettingModel.getEmailAvailable()">
        <b-form-radio-group
          :checked="notificationSettingModel.getEmailDisabled()"
          class="settings-content mt-2 mb-2"
          :disabled="isViewMode"
          @input="updateEmail"
        >
          <b-form-radio
            v-sr-only="emailYesSwitchedAriaText"
            :value="true"
            :class="isViewMode ? 'no-disabled-style' : ''"
            :aria-label="emailYesAriaText"
          >
            {{ $getText('NOTIFICATION_SETTINGS_YES') }}
          </b-form-radio>
          <b-form-radio
            v-sr-only="emailNoSwitchedAriaText"
            :value="false"
            :class="isViewMode ? 'no-disabled-style' : ''"
            :aria-label="emailNoAriaText"
          >
            {{ $getText('NOTIFICATION_SETTINGS_NO') }}
          </b-form-radio>
        </b-form-radio-group>
        <template v-if="!isViewMode || notificationSettingModel.getEmailDisabled()">
          <b-alert variant="danger" :show="areAllEmailsEmpty" class="settings-content email-required">
            {{ emailRequiredLabel }}
          </b-alert>
          <NotificationContactForm
            v-for="institutionProfile in institutionProfiles"
            :key="`contact-${institutionProfile.id}`"
            class="settings-content"
            :institution-profile="institutionProfile"
            :show-institution-name="institutionProfiles.length > 1"
            :disabled="isViewMode || !institutionProfile.currentUserCanEditContactInformation"
            :user-role="profile.role"
            @input="emitEmailInput"
          />
        </template>
        <template v-if="notificationSettingModel.getEmailDisabled()">
          <div class="settings-content font-weight-bold my-2">
            {{ $getText('NOTIFICATION_SETTINGS_FREQUENCY') }}
          </div>
          <b-form-radio-group
            :checked="notificationSettingModel.getInstant()"
            class="settings-content mb-3"
            :disabled="isViewMode"
            @input="updateInstant"
          >
            <b-form-radio
              v-sr-only:pre="receiveEmailAriaText"
              v-sr-only="selectedOnOffAriaText(notificationSettingModel.getInstant())"
              :value="true"
              :class="isViewMode ? 'no-disabled-style' : ''"
              :aria-label="emailAlwaysAriaText"
            >
              {{ $getText('NOTIFICATION_SETTINGS_ALWAYS') }}
            </b-form-radio>
            <b-form-radio
              v-sr-only:pre="receiveEmailAriaText"
              v-sr-only="selectedOnOffAriaText(!notificationSettingModel.getInstant())"
              :value="false"
              :class="isViewMode ? 'no-disabled-style' : ''"
              :aria-label="emailIntervalAriaText"
            >
              {{ $getText('NOTIFICATION_SETTINGS_INTERVAL') }}
            </b-form-radio>
          </b-form-radio-group>
          <template v-if="!notificationSettingModel.getInstant()">
            <div class="settings-content font-weight-bold mt-2">
              {{ $getText('NOTIFICATION_SETTINGS_TIME') }}
            </div>
            <table class="settings-content weekday-settings" :class="showNoDaySelected ? 'red-border' : ''">
              <tr>
                <td>
                  <b-form-checkbox
                    v-sr-only:pre="receiveEmailAriaText"
                    v-sr-only="selectedOnOffAriaText(everyDay)"
                    :checked="everyDay"
                    :disabled="isViewMode"
                    :class="isViewMode ? 'no-disabled-style' : ''"
                    :aria-label="emailAllDaysAriaText"
                    @input="updateEveryDay"
                    @change="updateModelDays"
                  >
                    {{ $getText('NOTIFICATION_SETTINGS_EVERYDAY') }}
                  </b-form-checkbox>
                </td>
                <td>
                  <notification-email-day
                    id="id-monday"
                    :checked="notificationSettingModel.getMonday()"
                    text-key-name="NOTIFICATION_SETTINGS_MONDAY"
                    :is-view-mode="isViewMode"
                    @change="updateMonday"
                  />
                </td>
              </tr>
              <tr>
                <td>
                  <notification-email-day
                    id="id-tuesday"
                    :checked="notificationSettingModel.getTuesday()"
                    text-key-name="NOTIFICATION_SETTINGS_TUESDAY"
                    :is-view-mode="isViewMode"
                    @change="updateTuesday"
                  />
                </td>
                <td>
                  <notification-email-day
                    id="id-wednesday"
                    :checked="notificationSettingModel.getWednesday()"
                    text-key-name="NOTIFICATION_SETTINGS_WEDNESDAY"
                    :is-view-mode="isViewMode"
                    @change="updateWednesday"
                  />
                </td>
              </tr>
              <tr>
                <td>
                  <notification-email-day
                    id="id-thursday"
                    :checked="notificationSettingModel.getThursday()"
                    text-key-name="NOTIFICATION_SETTINGS_THURSDAY"
                    :is-view-mode="isViewMode"
                    @change="updateThursday"
                  />
                </td>
                <td>
                  <notification-email-day
                    id="id-friday"
                    :checked="notificationSettingModel.getFriday()"
                    text-key-name="NOTIFICATION_SETTINGS_FRIDAY"
                    :is-view-mode="isViewMode"
                    @change="updateFriday"
                  />
                </td>
              </tr>
              <tr>
                <td>
                  <notification-email-day
                    id="id-saturday"
                    :checked="notificationSettingModel.getSaturday()"
                    text-key-name="NOTIFICATION_SETTINGS_SATURDAY"
                    :is-view-mode="isViewMode"
                    @change="updateSaturday"
                  />
                </td>
                <td>
                  <notification-email-day
                    id="id-sunday"
                    :checked="notificationSettingModel.getSunday()"
                    text-key-name="NOTIFICATION_SETTINGS_SUNDAY"
                    :is-view-mode="isViewMode"
                    @change="updateSunday"
                  />
                </td>
              </tr>
              <tr>
                <td colspan="2">
                  <div class="font-weight-bold mb-2">
                    {{ $getText('MOBILE_NOTIFICATION_SETTINGS_TIME_LABEL') }}
                  </div>
                  <AulaTimepicker
                    id="startTime"
                    class="notification-timepicker"
                    :value="notificationSettingModel.getScheduledTime()"
                    drop-direction="up"
                    format="HH:mm"
                    hour-label="Time"
                    minute-label="Minut"
                    :placeholder="$getText('MESSAGE_PLACEHOLDER_SELECT_TIME')"
                    :aria-label="$getText('MESSAGE_PLACEHOLDER_SELECT_TIME')"
                    hide-clear-button
                    :minute-interval="timepickerSettings.HALF_HOUR_INTERVAL"
                    advanced-keyboard
                    lazy
                    :class="isViewMode ? 'no-disabled-style' : ''"
                    :disabled="isViewMode"
                    @input="updateScheduledTime"
                  />
                </td>
              </tr>
            </table>
            <br />
            <b-alert variant="danger" :show="showNoDaySelected">
              {{ $getText('NOTIFICATION_SETTINGS_SAVE_FAILED') }}
            </b-alert>
          </template>
        </template>
      </template>
    </div>
    <aula-modal
      ref="cancelModal"
      ok-text="BUTTON_YES"
      @cancelClicked="$refs.cancelModal.hide()"
      @okClicked="cancelModalOkClicked()"
    >
      {{ $getText('CONSENT_CANCEL_MODAL_TEXT1') }}<br />
      {{ $getText('CONSENT_CANCEL_MODAL_TEXT2') }}
    </aula-modal>
    <aula-modal
      ref="confirmationModal"
      header-text="WARNING_MODAL"
      ok-text="BUTTON_YES"
      cancel-text="CANCEL"
      @cancelClicked="$refs.confirmationModal.hide()"
      @okClicked="deleteNotificationsFromModule"
    >
      {{ $getText('NOTIFICATION_CLEAR_ALL_WARNING_1') }}<br />
      {{ $getText('NOTIFICATION_CLEAR_ALL_WARNING_2') }}
    </aula-modal>
  </div>
</template>

<script>
import { portalRoles } from '../../enums/portalRoles';
import srOnly from '../../directives/sr-only';
import { timepickerSettings } from '../../enums/timepickerSettings';
import { notificationProviderKeyEnum } from '../../enums/notificationProviderKeyEnum';
import AulaButton from '../../components/AulaButton';
import AulaTimepicker from '../../components/Timepicker';
import NotificationCheckbox from './NotificationCheckbox';
import NotificationEmailDay from './NotificationEmailDay';
import NotificationContactForm from './NotificationContactForm.vue';

export default {
  name: 'NotificationSettingBrowser',
  directives: {
    srOnly,
  },
  components: {
    NotificationContactForm,
    AulaButton,
    AulaTimepicker,
    NotificationCheckbox,
    NotificationEmailDay,
  },
  inject: {
    getNotificationSettingModel: notificationProviderKeyEnum.NOTIFICATION_SETTING_MODEL,
  },
  props: {
    isLoading: { type: Boolean, default: false },
    isViewMode: { type: Boolean, default: false },
    availableAreas: { type: Array, default: () => [] },
    profile: { type: Object, default: null },
    everyDay: { type: Boolean, default: null },
    showNoDaySelected: { type: Boolean, default: null },
    areAllEmailsEmpty: { type: Boolean, default: false },
    institutionProfiles: { type: Array, default: () => [] },
  },
  emits: ['emailInput'],
  data: function () {
    return {
      portalRoles,
      timepickerSettings,
    };
  },
  computed: {
    emailRequiredLabel() {
      return this.$t('settings.notification_settings_email_required');
    },
    notificationSettingModel() {
      return this.getNotificationSettingModel();
    },
    emailYesAriaText() {
      const ariaTexts = [this.$getText('NOTIFICATION_SETTINGS_YES'), this.$getText('ARIA_NOTIFICATION_FOR_EMAIL')];
      return ariaTexts.join(' ');
    },
    emailNoAriaText() {
      const ariaTexts = [this.$getText('NOTIFICATION_SETTINGS_NO'), this.$getText('ARIA_NOTIFICATION_FOR_EMAIL')];
      return ariaTexts.join(' ');
    },
    emailYesSwitchedAriaText() {
      const ariaTexts = [this.$getText('ARIA_NOTIFICATION_FOR_EMAIL'), this.selectedOnOffAriaText(true)];
      return ariaTexts.join(' ');
    },
    emailNoSwitchedAriaText() {
      const ariaTexts = [this.$getText('ARIA_NOTIFICATION_FOR_EMAIL'), this.selectedOnOffAriaText(false)];
      return ariaTexts.join(' ');
    },
    emailAlwaysAriaText() {
      const ariaTexts = [this.receiveEmailAriaText, this.$getText('NOTIFICATION_SETTINGS_ALWAYS')];
      return ariaTexts.join(' ');
    },
    emailIntervalAriaText() {
      const ariaTexts = [this.receiveEmailAriaText, this.$getText('NOTIFICATION_SETTINGS_INTERVAL')];
      return ariaTexts.join(' ');
    },
    receiveEmailAriaText() {
      return this.$getText('ARIA_NOTIFICATION_RECEIVE_EMAIL');
    },
    emailAllDaysAriaText() {
      const ariaTexts = [this.receiveEmailAriaText, this.$getText('NOTIFICATION_SETTINGS_EVERYDAY')];
      return ariaTexts.join(' ');
    },
  },
  methods: {
    emitEmailInput(form) {
      this.$emit('emailInput', form);
    },
    confirmationModalOpen() {
      this.$refs.confirmationModal.show();
    },
    selectedOnOffAriaText(value) {
      return value
        ? this.$getText('ARIA_NOTIFICATION_SETTINGS_SELECTED_ON')
        : this.$getText('ARIA_NOTIFICATION_SETTINGS_SELECTED_OFF');
    },
    cancelModalOkClicked() {
      this.updateViewMode(true);
      this.$refs.cancelModal.hide();
      this.$emit('resetTable');
    },
    deleteNotificationsFromModule() {
      this.$emit('deleteNotificationsFromModule');
      this.$refs.confirmationModal.hide();
    },
    initEditMode() {
      this.$emit('initEditMode');
    },
    saveChanges() {
      this.$emit('saveChanges');
    },
    updateViewMode(value) {
      this.$emit('updateViewMode', value);
    },
    updateEveryDay(value) {
      this.$emit('updateEveryDay', value);
    },
    updateModelDays() {
      this.$emit('updateModelDays');
    },
    updateAppDisabled(value) {
      this.notificationSettingModel.setAppDisabled(value);
    },
    updateCalendar(value) {
      this.notificationSettingModel.setCalendar(value);
    },
    updateEmail(value) {
      this.notificationSettingModel.setEmailDisabled(value);
    },
    updateGallery(value) {
      this.notificationSettingModel.setGallery(value);
    },
    updateWidget(widgetId, value) {
      this.notificationSettingModel.setWidgetSettingById(widgetId, value);
    },
    updateInstant(value) {
      this.notificationSettingModel.setInstant(value);
    },
    updateMessagesFromChildren(value) {
      this.notificationSettingModel.setMessagesFromChildren(value);
    },
    updateMessagesFromEmployees(value) {
      this.notificationSettingModel.setMessagesFromEmployees(value);
    },
    updateMessagesFromGuardians(value) {
      this.notificationSettingModel.setMessagesFromGuardians(value);
    },
    updateNotifyAssignedAsSubstituteTeacher(value) {
      this.notificationSettingModel.setNotifyAssignedAsSubstituteTeacher(value);
    },
    updateNotifyLessonNoteChanged(value) {
      this.notificationSettingModel.setNotifyLessonNoteChanged(value);
    },
    updatePickUpActivity(value) {
      this.notificationSettingModel.setPickUpActivity(value);
    },
    updatePost(value) {
      this.notificationSettingModel.setPost(value);
    },
    updateScheduledTime(value) {
      this.notificationSettingModel.setScheduledTime(value);
    },
    updateSelectedAreas(value) {
      this.notificationSettingModel.setSelectedAreas(value);
    },
    updateVacationRequest(value) {
      this.notificationSettingModel.setVacationRequest(value);
    },
    updateMonday(value) {
      this.notificationSettingModel.setMonday(value);
    },
    updateTuesday(value) {
      this.notificationSettingModel.setTuesday(value);
    },
    updateWednesday(value) {
      this.notificationSettingModel.setWednesday(value);
    },
    updateThursday(value) {
      this.notificationSettingModel.setThursday(value);
    },
    updateFriday(value) {
      this.notificationSettingModel.setFriday(value);
    },
    updateSaturday(value) {
      this.notificationSettingModel.setSaturday(value);
    },
    updateSunday(value) {
      this.notificationSettingModel.setSunday(value);
    },
  },
};
</script>

<style scoped lang="scss">
@use '../../assets/scss/core/variables' as *;
@use '../../assets/scss/core/breakpoints' as *;
@use '../../assets/scss/elements/notificationSetting' as *;

footer {
  @include breakpoint-lg-down() {
    position: fixed !important;
    bottom: 80px;
    top: unset;
  }
}

.settings-label {
  display: block;
  &.large {
    margin-bottom: 15px;
  }
}

.red-border {
  .notification-settings-body-mobile & {
    display: block;
  }
}

.weekday-settings {
  margin-top: -8px;
}
.notification-timepicker {
  max-width: 320px;
}
</style>
